import React, { useState, useEffect, useRef } from 'react';
import { string, any, func, bool } from 'prop-types';

import Caption from './Caption';
import classnames from 'classnames'
import InfoIcon from '../icons/InfoIcon';

import styled, { keyframes } from "styled-components";

const placeholder = '/images/builder/placeholder.jpg';

import { debounce } from 'lodash';

const loadingProcessAnimation = keyframes`
    0% {
      opacity: 0.1;
    }
    100% {
      opacity: 1;
    }
`;

const loadingSuccessAnimation = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 0.5;
    }
`;

const Image = styled.img`
  display: block;
  height: auto;
  width: 100%;
  object-fit: cover;
  border: 1px solid white;
  border-radius: 2.66px;



  &.loaded {
    animation-name: ${loadingProcessAnimation};
    animation-timing-function: ease-in;
    animation-duration: 0.3s;
  }

  &.loading {
      animation-name: ${loadingSuccessAnimation};
      animation-timing-function: ease-in;
      animation-duration: 0.2s;
  }

  &.has-error {
    content: url(${placeholder});
  }

  @media (max-width: 768px) {
    height: 100%;
  }
`;


const LazyImageContainer = ({
    article,
    value,
    alt,
    src,
    onChange,
    onFabricInfoClicked,
    onFabricHoverIn,
    onFabricHoverOut,
    checked,
}) => {
    const [hovered, setHovered] = useState(false);
    const [isVisible, setIsVIsible] = useState(false);

    const rootRef = useRef();

    const mouseOver = () => {
        setHovered(true);
        defferedHoverIn();
    }

    const mouseLeave = (e) => {
        setHovered(false);
        if (rootRef.current.className === e.target.className) {
            defferedHoverOut();
        }
    }

    const defferedHoverIn = debounce(onFabricHoverIn, 100);
    const defferedHoverOut = debounce(onFabricHoverOut, 0);

    useEffect(
        () => {
            const node = rootRef.current;

            if (node) {
                node.addEventListener('mouseover', mouseOver)
                node.addEventListener('mouseout', mouseLeave)

                return () => {
                    node.removeEventListener('mouseover', mouseOver)
                    node.removeEventListener('mouseout', mouseLeave)
                }
            }
        }, [rootRef.current, hovered])

    const infoClassName = classnames('lazy-image-list__info', {
        'lazy-image-list__info_hovered': hovered
    });

    useEffect(
        () => {
            let observer;
            if (IntersectionObserver) {
                observer = new IntersectionObserver(
                    entries => {
                        entries.forEach(entry => {
                            if (
                                (entry.intersectionRatio > 0 || entry.isIntersecting)
                            ) {
                                setTimeout(() => setIsVIsible(true), 100)
                                observer.unobserve(rootRef.current);
                            }
                        });
                    },
                    {
                        threshold: 0.01,
                        rootMargin: "100%"
                    }
                );
                observer.observe(rootRef.current);
            }
            return () => {
                if (observer && observer.unobserve) {
                    observer.unobserve(rootRef.current);
                }
            };
        },
        [src, rootRef]
    );

    return (
        <div className={
            `lazy-image-list__item ${checked ? 'lazy-image-list__item_checked' : ''} item-theme--bordered`
        }>
            {
                checked && <span className='lazy-image-list__check-label' />
            }
            <label ref={rootRef} className='lazy-image-list__label'>
                {hovered && <Caption caption={article} />}
                <input
                    type='radio'
                    checked={checked}
                    value={value}
                    className='lazy-image-list__fake-input'
                    onChange={
                        (e) => {
                            e.preventDefault();
                            onChange(e.target.value);
                        }
                    }
                />

                <Image
                    src={isVisible ? src : placeholder}
                    className={!isVisible ? 'loading' : 'loaded'}
                    data-src={src}
                    alt={alt}
                />
                <InfoIcon
                    onClick={(e) => {
                        e.preventDefault();
                        onFabricInfoClicked()
                    }}
                    className={infoClassName}
                />
            </label>

        </div>
    );
}

LazyImageContainer.propTypes = {
    article: string,
    value: any,
    alt: string,
    src: string,
    onChange: func,
    onFabricInfoClicked: func,
    onFabricHoverIn: func,
    onFabricHoverOut: func,
    checked: bool,
}

LazyImageContainer.defaultProps = {
    rootClassName: 'lazy-image-list__item',
}

export default LazyImageContainer;